import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  type LabelList,
  labelListsQueries,
  normalizeLabelLists,
} from '@repo/api-kit/queries/label-lists';
import type { Label } from '@repo/api-kit/graphql/types';
import { client } from '@repo/api-kit/queries/utils';
import { calculateVatAmount } from '#accounts-payable/utils/calculate-vat-amount';
import { useCustomLabelAbility } from '#abilities/use-custom-label';
import { useMyPermissions } from '#abilities/common/use-my-permissions';
import { useCurrentOrganizationId } from '#organizations';
import { buildVatAccountingProps } from '../vat-accounting/build-vat-accounting-props';
import { getLabelsToUpdateById } from '../sidebar-labels/utils/persist-labels';
import type { BookkeepingSectionProps } from './bookkeeping-section';

interface VatRatesCategoriesResponse {
  vatRatesCategories: { value: number }[];
}

const vatRatesCategoriesQuery = queryOptions({
  queryKey: ['vat-rates-categories'] as const,
  queryFn: async () => {
    const data = await client<VatRatesCategoriesResponse>('/vats/v1/vat_rates_categories');
    return data.vatRatesCategories.map(category => category.value);
  },
  staleTime: Infinity,
});

interface UseBookkeepingSectionPropsParams {
  vatRate: number | null;
  vatAmount: number | null;
  note: string;
  labels: Label[];
  amount: number;
  onVatChange: (next: { vatRate: number | null; vatAmount: number | null }) => void;
  onNoteChange: (note: string) => void;
  onLabelsChange: (labels: Label[]) => void;
  /** When false, the internal note field is hidden (e.g. request flows). Defaults to true. */
  showInternalNote?: boolean;
}

/**
 * Builds the props for `<BookkeepingSection>` from a flow-owned state shape.
 *
 * The hook is intentionally **state-container-agnostic**: it never reads any
 * specific React context. Each flow (PayByInvoice today, Pay Later later)
 * passes its own state and change callbacks. This keeps the component +
 * util + hook stack reusable across flows without a shared context.
 *
 * Returns `undefined` when neither VAT nor custom labels can be shown — in
 * which case the caller is expected to skip rendering `<BookkeepingSection>`
 * altogether (the consumer pattern in `<BaseForm>` already does this).
 */
export function useBookkeepingSectionProps({
  vatRate,
  vatAmount,
  note,
  labels,
  amount,
  onVatChange,
  onNoteChange,
  onLabelsChange,
  showInternalNote = true,
}: UseBookkeepingSectionPropsParams): BookkeepingSectionProps | undefined {
  const { formatMessage } = useIntl();
  const organizationId = useCurrentOrganizationId();
  const { canUse: canUseCustomLabels } = useCustomLabelAbility();
  const permissions = useMyPermissions();
  const canUseVat = permissions.transaction_vat.access;

  const { data: vatRatesCategoriesData } = useQuery({
    ...vatRatesCategoriesQuery,
    enabled: canUseVat,
  });
  const vatRatesCategories = useMemo(() => vatRatesCategoriesData ?? [], [vatRatesCategoriesData]);

  const { data: labelListsResponse } = useQuery({
    ...labelListsQueries.list(organizationId),
    enabled: canUseCustomLabels,
  });

  const labelLists = useMemo<LabelList[]>(() => {
    if (!labelListsResponse) return [];
    return normalizeLabelLists(labelListsResponse);
  }, [labelListsResponse]);

  // Mirror Ember: when the gross amount changes and a VAT rate is selected,
  // recompute the VAT amount. Watching `[amount]` only — rate changes are
  // handled by `<VatAccounting>`'s `onSelect` (via `computeVatAmountOnSelect`),
  // and watching `vatRate` here would double-dispatch.
  // See `apps/qonto-js/.../payment-details.ts:1043-1046`.
  useEffect(() => {
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (vatRate === null || vatRate <= 0) return;
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    const next = calculateVatAmount(amount, vatRate);
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (next !== vatAmount) {
      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      onVatChange({ vatRate, vatAmount: next });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- intentional: only recalc on amount change
  }, [amount]);

  const vatAccountingProps = useMemo(
    () =>
      buildVatAccountingProps({
        vatRate,
        vatAmount,
        amount,
        vatRatesCategories,
        onVatChange,
        formatMessage,
      }),
    [vatRate, vatAmount, amount, vatRatesCategories, onVatChange, formatMessage]
  );

  const handleSaveLabel = useMemo(
    () => (labelList: LabelList, label: Label | null) => {
      // oxlint-disable-next-line typescript/consistent-type-assertions
      const nextLabels = getLabelsToUpdateById(labels, labelList, label ?? undefined) as Label[];
      onLabelsChange(nextLabels);
    },
    [labels, onLabelsChange]
  );

  if (!canUseVat && !canUseCustomLabels) return undefined;

  return {
    vatAccountingProps,
    labelLists,
    selectedLabels: labels,
    onSaveLabel: handleSaveLabel,
    showVat: canUseVat,
    showCustomLabels: canUseCustomLabels,
    showInternalNote,
    note,
    onNoteChange,
  };
}
